/**
 * 路由数据转换工具
 * 将 title、isHide、isLink 移到 meta 对象中
 */

/**
 * 原始路由对象类型
 */
interface OriginalRoute {
  id?: string
  pid?: string
  value?: any
  children?: OriginalRoute[] | null
  path?: string | null
  name?: string | null
  component?: string | null
  title?: string | null
  icon?: string | null
  codeNo?: string | null
  level?: any
  platformType?: string | null
  resourceType?: string | null
  link?: string | null
  redirect?: string | null
  isIframe?: boolean | null
  isAffix?: boolean | null
  isKeepAlive?: boolean | null
  isHide?: boolean | null
  isLink?: string | null
  roles?: string[] | null
}

/**
 * 转换后的路由对象类型
 */
interface TransformedRoute extends Omit<OriginalRoute, 'title' | 'isHide' | 'isLink'> {
  meta: {
    title?: string | null
    isHide?: boolean | null
    isLink?: string | null
    isAffix?: boolean | null
    isKeepAlive?: boolean | null
    isIframe?: boolean | null
    roles?: string[] | null
    icon?: string | null
  }
}

/**
 * 转换路由数据，将 title、isHide、isLink 移到 meta 对象中
 * 递归处理所有层级
 * @param routes 原始路由数组
 * @returns 转换后的路由数组
 */
export function transformRoutes(routes: OriginalRoute[]): TransformedRoute[] {
  if (!Array.isArray(routes)) return routes

  return routes
    .filter((route) => route.resourceType !== '2' && route.platformType !== '2' && route.isLink != '1')
    .map((route) => {
      // 解构出需要移到 meta 的字段
      const { title, isHide, isLink, isAffix, isKeepAlive, isIframe, roles, icon, pid, hideChildrenInMenu, ...rest } = route

      // 创建新的路由对象
      const newRoute: any = {
        ...rest,
        pid,
        hideChildrenInMenu: hideChildrenInMenu === '1' ? true : false,
        meta: {
          title,
          isHide,
          isLink,
          isAffix,
          isKeepAlive,
          isIframe,
          roles,
          icon
        }
      }

      // 递归处理 children
      if (newRoute.children && Array.isArray(newRoute.children)) {
        newRoute.children = transformRoutes(newRoute.children)
      }

      return newRoute
    })
}

export function transformRoutesMap(routes: OriginalRoute[]): TransformedRoute[] {
  if (!Array.isArray(routes)) return routes
  return routes
    .filter((route) => route.isLink == '1')
    .map((route) => {
      // 解构出需要移到 meta 的字段
      const { title, isHide, isLink, isAffix, isKeepAlive, isIframe, icon, ...rest } = route

      // 创建新的路由对象
      const newRoute: TransformedRoute = {
        ...rest,
        meta: {
          title,
          isHide,
          isLink,
          isAffix,
          isKeepAlive,
          isIframe,
          icon
        }
      }

      // 递归处理 children
      if (newRoute.children && Array.isArray(newRoute.children)) {
        newRoute.children = transformRoutes(newRoute.children)
      }

      return newRoute
    })
}

/**
 * 转换整个响应数据结构
 * @param response 原始响应数据
 * @returns 转换后的响应数据
 */
export function transformRouteResponse(response: { code: number; msg: string | null; data: OriginalRoute[] }): {
  code: number
  msg: string | null
  data: TransformedRoute[]
} {
  return {
    ...response,
    data: transformRoutes(response.data)
  }
}

/**
 * 提取 resourceType === '2' 的按钮权限 codeNo 集合
 * @param routes 原始路由数组
 * @returns 按钮权限 codeNo 数组
 */
export function extractButtonCodeNos(routes: OriginalRoute[]): string[] {
  const codeNos: string[] = []

  function traverse(items: OriginalRoute[]) {
    if (!Array.isArray(items)) return

    items.forEach((item) => {
      if (item.resourceType === '2' && item.codeNo) {
        codeNos.push(item.codeNo)
      }
      if (item.children && item.children.length > 0) {
        traverse(item.children)
      }
    })
  }

  traverse(routes)
  return codeNos
}

export default transformRoutes
